import { formatChipsShort } from '@polf/shared/locale'

import type { PokerPlayerTableSeatFragment } from '../../../__generated__/hooks'

/**
 * "Mitt bord" grid — 2-col layout of the players still ALIVE at the auth
 * player's table. Busted players leave the table physically, so they're
 * dropped from the roster (showing greyed-out knocked-out rows here cluttered
 * the live table view). Your seat gets a green ring + "Du" pill.
 *
 * Stack values:
 *   - tablemates → `startingStack`. `currentStack` isn't exposed per
 *     table seat in the player view — deliberate, avoids revealing
 *     live sizes around the table.
 *   - your own row → `myCurrentStack` (passed in from ActiveView's
 *     `view.seat.currentStack`). Stays consistent with the SeatCard
 *     hero above; without this, editing your stack updated SeatCard
 *     but not the "Du"-highlighted row here.
 */
const TableGrid = ({
  tableNumber,
  seats,
  myCurrentStack,
}: {
  tableNumber: number
  seats: ReadonlyArray<PokerPlayerTableSeatFragment>
  myCurrentStack: number | null
}) => {
  const alive = seats.filter((s) => !s.eliminated)
  if (!alive.length) return null
  return (
    <div className="mx-2 my-3 bg-paper rounded-lg shadow overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-ink-100">
        <span className="up text-ink-500">Mitt bord · Bord {tableNumber}</span>
        <span className="text-xs text-ink-500 num">{alive.length} aktive</span>
      </div>
      <div className="grid grid-cols-2 gap-2 p-3">
        {alive.map((s) => {
          const cls = s.isYou
            ? 'border-green-700 ring-2 ring-green-700 bg-green-50'
            : 'border-ink-200 bg-paper text-ink-900'
          const stackForRow =
            s.isYou && myCurrentStack != null ? myCurrentStack : s.startingStack
          return (
            <div
              key={s.id}
              className={`relative rounded-lg px-3 py-2.5 flex flex-col gap-0.5 border ${cls}`}
            >
              <div className="flex items-center gap-2">
                <span className="font-mono num text-[10px] font-bold uppercase text-ink-500">
                  #{s.seatNumber}
                </span>
                {s.isYou ? (
                  <span className="text-[9px] font-bold uppercase tracking-wider text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">
                    Du
                  </span>
                ) : null}
              </div>
              <div className="text-sm font-semibold truncate">
                {s.shortName}
              </div>
              <div className="font-mono num text-xs">
                {formatChipsShort(stackForRow)}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default TableGrid
